import { CSSProperties, ReactNode } from 'react';

import { clsx } from 'clsx';

import styles from './OptionCards.module.css';

export type TOption<TValue extends string> = {
  value: TValue;
  label: ReactNode;
  icon?: ReactNode;
};

type TOptionCardsProps<TValue extends string> = {
  name: string;
  options: TOption<TValue>[];
  value: TValue | undefined;
  onChange: (value: TValue) => void;
  /** Lay the cards out in this many equal columns instead of a vertical list. */
  columns?: number;
  /** Hide the trailing radio dot and centre the label — for compact toggles. */
  hideRadio?: boolean;
};

/**
 * A single-select list of tappable radio cards (loan duration, loan purpose).
 * Real radio inputs for keyboard/screen-reader support; selection is driven by
 * the checked state.
 *
 * Defaults to a vertical list with a trailing radio dot. Pass `columns` for a
 * side-by-side segmented layout and `hideRadio` to drop the dot (e.g. the
 * Herr/Frau salutation toggle).
 *
 * Not orion's `Radio` — its icon-capable compound API isn't in our pinned
 * version. Swap once orion is upgraded.
 */
export const OptionCards = <TValue extends string>({
  name,
  options,
  value,
  onChange,
  columns,
  hideRadio = false,
}: TOptionCardsProps<TValue>) => (
  <div
    className={clsx(styles.options, { [styles.grid]: columns })}
    role="radiogroup"
    style={columns ? ({ '--columns': columns } as CSSProperties) : undefined}
  >
    {options.map((option) => (
      <label
        key={option.value}
        className={clsx(styles.card, {
          [styles.selected]: value === option.value,
          [styles.centered]: hideRadio,
        })}
      >
        <input
          type="radio"
          className={styles.input}
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        {option.icon && (
          <span className={styles.icon} aria-hidden>
            {option.icon}
          </span>
        )}
        <span className={styles.label}>{option.label}</span>
        {!hideRadio && <span className={styles.radio} aria-hidden />}
      </label>
    ))}
  </div>
);
